'use client';

import { Divider, Padding, Typography } from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import { AGREEMENT_LABELS, type AgreementKey } from '@musinsa/services-curator';
import { type ReactElement, type ReactNode, useMemo } from 'react';

import { AgreementModal } from '../../../../agreements';
import { getPaths, useIsDesktop } from '../../../../common';
import { AgreementItem } from './AgreementItem';

interface AgreementSectionProps {
  termsAgreements: Record<AgreementKey, boolean>;
  onToggle: (type: AgreementKey | 'all') => void;
  className?: string;
}

type AgreementModalComponent = ({ children }: { children: ReactNode }) => ReactElement | null;

const AGREEMENT_MODAL_COMPONENTS: Partial<Record<AgreementKey, AgreementModalComponent>> = {
  SERVICE_TERMS: ({ children }) => <AgreementModal type="terms">{children}</AgreementModal>,
  SERVICE_POLICY: ({ children }) => <AgreementModal type="policy">{children}</AgreementModal>,
};

export const AgreementSection = ({
  termsAgreements,
  onToggle,
  className,
}: AgreementSectionProps) => {
  const { service } = useDeviceInfo();
  const paths = getPaths(service);
  const agreementKeys = useMemo(() => Object.keys(AGREEMENT_LABELS) as AgreementKey[], []);

  const agreementDetailLinks: Record<AgreementKey, string> = {
    SERVICE_TERMS: paths.terms(),
    SERVICE_POLICY: paths.policy(),
  };
  const isAllAgreed = agreementKeys.every((key) => termsAgreements[key]);
  // desktop 웹 = 새 탭 / 모바일·웹뷰 = 모달. 시딩 약관과 공용 추출. ([[useIsDesktop]])
  const isDesktop = useIsDesktop();

  return (
    <div className={className}>
      <div className="flex flex-col">
        <AgreementItem
          label="약관 전체 동의하기(필수)"
          checked={isAllAgreed}
          onClick={() => onToggle('all')}
          showDetailButton={false}
        />

        {agreementKeys.map((key) => {
          const label = AGREEMENT_LABELS[key];
          const ModalComponent = AGREEMENT_MODAL_COMPONENTS[key];
          const detailLink = agreementDetailLinks[key];

          const detailTrigger = (
            <Typography
              variant="body13pxReg"
              colorScheme="gray-600"
              className="cursor-pointer"
              textDecoration="underline"
            >
              자세히
            </Typography>
          );

          return (
            <div key={key} className="flex items-center justify-between pl-6">
              <div className="flex items-center gap-2">
                <AgreementItem
                  label={label}
                  checked={termsAgreements[key]}
                  onClick={() => onToggle(key)}
                  showDetailButton={false}
                />
              </div>
              <div className="flex-shrink-0">
                {isDesktop && detailLink ? (
                  <button type="button" onClick={() => window.open(detailLink, '_blank')}>
                    {detailTrigger}
                  </button>
                ) : (
                  ModalComponent && <ModalComponent>{detailTrigger}</ModalComponent>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Padding y="3">
        <Divider variant="line" />
      </Padding>
      <Padding top="1" bottom="3">
        <Typography variant="body13pxReg" colorScheme="gray-600">
          전체 동의는 본 서비스 활동을 위한 필수 동의사항이 포함 되어 있습니다. 필수사항의 동의를
          거부 할 권리가 있습니다. 다만, 동의를 거부할 경우 본 서비스 신청이 불가합니다.
        </Typography>
      </Padding>
    </div>
  );
};
